// What the Billing screen says about what Naurist sells — everything about it
// that is not a price.
//
// The prices used to be here too, as a list the app shipped with, because there
// was no endpoint behind them. There is one now: `GET /api/v1/billing/plans/`,
// read by `plansService`, and it is the only place a figure comes from. Nothing
// in this feature writes one down any more — a price list in a bundle is a
// price somebody is still being quoted from cache the day it changes.
//
// What is left is copy. The feature list is ours to write and the backend has
// no opinion on it; the product name and the cycle labels are how this app says
// out loud what the plan rows mean.

import type { BillingCycleId, PlanFeature } from "../types/billing.types";

/**
 * What the subscription includes. One list, shared by both cards: there is a
 * single product and the cycles differ only in what they cost, which is what
 * "One plan. Complete access" above it is saying.
 */
export const PRO_FEATURES: PlanFeature[] = [
  {
    name: "Client management",
    description:
      "Keep client profiles, consultation details, and payment records organised.",
  },
  {
    name: "Personalised meal planning",
    description:
      "Create customised meal plans powered by accurate nutrition data.",
  },
  {
    name: "Dish & nutrition database",
    description:
      "Access a growing database of dishes, ingredients, and nutrient values.",
  },
  {
    name: "Branded Documents",
    description: "Share meal plans and invoices with your own logo and branding.",
  },
  {
    name: "Live Support",
    description: "Get help whenever you need it from our support team.",
  },
];

/**
 * What the product is called, for the screens that have to name it without a
 * plan in hand.
 *
 * The cards take the name off the plan the endpoint sent — that is the seller's
 * own word for what is on sale. This is for the plan *details* screen, which
 * names a subscription somebody already has: what they are paying for should
 * not go unnamed because a price list they are not being shown failed to load.
 */
export const PRODUCT_NAME = "Naurist Pro";

/** The cycle, as every screen writes it. Ours rather than the backend's, which
 *  says `MONTH` — a word for a machine, not for a card. */
export const CYCLE_LABELS: Record<BillingCycleId, string> = {
  monthly: "Monthly",
  yearly: "Yearly",
};

/**
 * The line of small print under a card's price, for the cycle that has one.
 *
 * The year is sold at the launch price *and* a further cut for committing to a
 * year, and the struck-through figure beside the price cannot say which of the
 * two is which — it is one discount by the time it reaches the card. This says
 * the second one out loud, on the card it applies to.
 *
 * Copy, like everything else in this file, and keyed by cycle rather than by
 * which card is being recommended: it is a claim about the yearly plan, not
 * about the layout. That does mean the figure in the sentence is written here
 * while the prices come off `GET /api/v1/billing/plans/` — the endpoint sends
 * one discount per term and not its parts, so there is nothing to derive it
 * from. If the yearly cut is ever re-priced away from 10%, this line has to
 * follow it.
 */
export const CYCLE_PRICE_NOTES: Partial<Record<BillingCycleId, string>> = {
  yearly: "Incl. extra 10% yearly discount",
};

/**
 * How long a refund can be asked for, as the bar under the cards says it.
 *
 * "Money-back guarantee" rather than "refund policy": the same seven days
 * either way, said as the promise it is rather than as the name of a document.
 */
export const REFUND_POLICY = "7-Day money-back guarantee";

/**
 * The seven days, as a number, for the copy that has to name them.
 *
 * Copy and nothing else. The days used to be counted here too — the browser
 * worked out from the invoice list and its own clock whether the window was
 * still open — and that is `GET /api/v1/billing/refund/`'s answer now: it says
 * whether the refund can be had and the moment it lapses, and the screen draws
 * what it is told. This is only how the promise is worded, which is why it can
 * safely live in the bundle: the number in a sentence is a policy, and the
 * decision it used to drive is no longer taken here.
 */
export const REFUND_WINDOW_DAYS = 7;
